"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeftIcon, Sparkles, ChartColumnIncreasing, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";
import { useInView } from 'react-intersection-observer';
import useSWR from 'swr';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MobileMenuButton } from "@/components/ui/mobile-menu-button";
import { useEpisodeProgress } from "@/hooks/useEpisodeProgress";
import { useUser } from "@/lib/context/user-context";
import { useUserSettings } from "@/contexts/user-settings-context";
import { usePlayer } from "@/contexts/player-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRouter } from "next/navigation";

const ENABLE_DIRECT_GLOBAL_PLAYER = true;

// 用于避免 hydration 不匹配的 hook
function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return hasMounted;
}

// 通用的数据获取函数
const fetcher = (url: string) => fetch(url).then(res => {
  if (!res.ok) throw new Error(`Failed to fetch episodes: ${res.statusText}`);
  return res.json();
});

interface Episode {
  ownerAvatarUrl: string;
  author: string;
  title: string;
  shareId: string;
  userId?: string;
  videoId?: string;
  thumbnail?: string;
  isFeaturedEpisode?: boolean;
  duration?: number;
  durationFormatted?: string;
  titleTranslation?: string;
}

interface EpisodesResponse {
  episodes: Episode[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

function EpisodeItem({ episode, isCompleted = false }: { episode: Episode; isCompleted?: boolean }) {
  const hasMounted = useHasMounted();
  const { t } = useI18n();
  const { userSettings } = useUserSettings();
  const { session } = useUser();
  const router = useRouter();
  const {
    setCurrentEpisode,
    setCleanReadData,
    setIsLoading: setPlayerLoading,
    openFullPlayer,
    closeFullPlayer,
  } = usePlayer();
  const isMobile = useIsMobile();
  const [isDirectLoading, setIsDirectLoading] = useState(false);
  
  // 只在客户端 mounted 后才启用 direct play，避免 hydration 不匹配
  const allowDirectPlay = hasMounted && ENABLE_DIRECT_GLOBAL_PLAYER && isMobile;

  const preferChineseTitles = hasMounted ? userSettings?.preferChineseTitles : false;
  const displayTitle =
    preferChineseTitles && episode.titleTranslation ? episode.titleTranslation : episode.title;
  const tooltipText = (() => {
    if (!episode.titleTranslation) return undefined;
    if (preferChineseTitles) return episode.title;
    return episode.titleTranslation;
  })();

  const episodeUrl = episode.videoId
    ? `/episodes/${episode.shareId}?videoId=${episode.videoId}`
    : `/episodes/${episode.shareId}`;

  const attemptDirectGlobalPlay = useCallback(async () => {
    if (!allowDirectPlay) {
      return false;
    }

    if (isDirectLoading) {
      return true;
    }

    try {
      setIsDirectLoading(true);
      openFullPlayer();
      setPlayerLoading(true);

      const placeholderEpisode = {
        shareId: episode.shareId,
        content: {
          shareId: episode.shareId,
          title: episode.title,
          author: episode.author,
          ownerAvatarUrl: episode.ownerAvatarUrl,
          thumbnail: episode.thumbnail,
          videoId: episode.videoId,
          duration: episode.duration,
        },
      };

      setCurrentEpisode(placeholderEpisode);
      setCleanReadData(null);
      setPlayerLoading(true);

      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      const token =
        session?.access_token ??
        (typeof window !== "undefined"
          ? window.localStorage.getItem("token")
          : null);

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/episodes/${episode.shareId}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch episode data: ${response.status}`);
      }

      const episodeData = await response.json();
      setCurrentEpisode(episodeData);
      setCleanReadData(episodeData?.content?.content?.cleanRead ?? null);

      return true;
    } catch (error) {
      console.error("Unable to open episode in global player:", error);
      closeFullPlayer();
      return false;
    } finally {
      setPlayerLoading(false);
      setIsDirectLoading(false);
    }
  }, [
    episode,
    isDirectLoading,
    session?.access_token,
    setCleanReadData,
    setCurrentEpisode,
    setPlayerLoading,
    openFullPlayer,
    closeFullPlayer,
    allowDirectPlay,
  ]);

  const handleCardClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!allowDirectPlay) {
        return;
      }

      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
        return;
      }

      e.preventDefault();

      const handled = await attemptDirectGlobalPlay();
      if (!handled) {
        if (typeof window !== "undefined") {
          window.location.href = episodeUrl;
        } else {
          router.push(episodeUrl);
        }
      }
    },
    [attemptDirectGlobalPlay, episodeUrl, router, allowDirectPlay]
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link 
          href={episodeUrl}
          onClick={handleCardClick}
          className="block w-full cursor-pointer group transition-all duration-200 hover:bg-primary/20 p-0 sm:p-1 rounded-lg"
        >
      {/* 水平卡片布局 */}
      <div className="flex gap-3 items-start">
        {/* 左侧缩略图 */}
        <div className="flex-shrink-0 w-[114px] h-[64px] rounded-md overflow-hidden relative self-start">
          <div className="w-full h-full relative">
            <Image 
              src={episode.thumbnail || "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=128&h=80&fit=crop&crop=center"} 
              alt={displayTitle}
              fill
              className="object-cover group-hover:scale-110 transition-transform duration-300"
              loading="lazy"
            />
          </div>
          
          {/* Loading overlay */}
          {allowDirectPlay && isDirectLoading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-white" />
            </div>
          )}
          
          {/* Duration badge - Bottom right corner */}
          {episode.durationFormatted && (
            <div className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
              {episode.durationFormatted}
            </div>
          )}
          
          {/* Featured Badge positioned over image */}
          {episode.isFeaturedEpisode && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent">
              <div className="p-1">
                <div className="bg-primary/90 text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-sm font-normal flex items-center justify-center gap-1">
                  <Sparkles className="h-2.5 w-2.5" />
                  <span>{t('home.free')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Quiz completion badge - Top right corner */}
          {isCompleted && (
            <div className="absolute top-1 right-1">
              <div className="bg-green-500 text-white rounded-md p-1">
                <CheckCircle2 className="h-3 w-3" />
              </div>
            </div>
          )}
        </div>
        
        {/* 右侧内容 */}
        <div className="flex-1 min-w-0">
          {/* 顶部标题 */}
          <h3 className="text-sm leading-tight line-clamp-2 font-medium group-hover:text-foreground transition-colors duration-200">
            {displayTitle}
          </h3>
          
          {/* 底部频道信息 */}
          <div className="flex items-center gap-2 mt-1">
            {/* 作者头像 */}
            <div className="flex-shrink-0 w-5 h-5 rounded-full overflow-hidden bg-primary/15">
              <Image
                src={episode.ownerAvatarUrl || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=20&h=20&fit=crop&crop=face"}
                alt={episode.author}
                width={20}
                height={20}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            
            {/* 作者名称 */}
            <span className="text-muted-foreground text-xs group-hover:text-muted-foreground/80 transition-colors duration-200 truncate">
              {episode.author}
            </span>
          </div>
        </div>
      </div>
        </Link>
      </TooltipTrigger>
      {tooltipText && (
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-normal">{tooltipText}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}

// 骨架屏组件
function SkeletonItem() {
  return (
    <div className="p-1">
      <div className="flex gap-3">
        <div className="flex-shrink-0 w-[114px] h-[64px] rounded-md bg-muted animate-pulse"></div>
        <div className="flex-1 py-1 space-y-2">
          <div className="bg-muted h-4 rounded animate-pulse"></div>
          <div className="bg-muted h-4 rounded w-3/4 animate-pulse"></div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-muted rounded-full animate-pulse"></div>
            <div className="bg-muted h-3 rounded w-20 animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EpisodesPage() {
  const { t, lang } = useI18n() as any;
  const { user } = useUser();
  const [allEpisodes, setAllEpisodes] = useState<Episode[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalEpisodes, setTotalEpisodes] = useState(0);
  const loadingRef = useRef(false);

  const { isEpisodeCompleted } = useEpisodeProgress(user?.id); // 防止重复加载

  // 获取第一页数据
  const { data: initialData, error: initialError, isLoading: isInitialLoading } = useSWR<EpisodesResponse>(
    `/api/trending?type=episodes&page=1&limit=20`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000,
    }
  );

  // 初始化数据
  useEffect(() => {
    if (initialData && initialData.episodes) {
      setAllEpisodes(initialData.episodes);
      setTotalEpisodes(initialData.pagination.total);
      setHasMore(initialData.pagination.hasMore);
    }
  }, [initialData]);

  // 加载更多数据的函数
  const loadMoreData = async () => {
    if (loadingRef.current || isLoadingMore || !hasMore) return;
    
    loadingRef.current = true;
    setIsLoadingMore(true);
    
    try {
      const nextPage = currentPage + 1;
      const response = await fetcher(`/api/trending?type=episodes&page=${nextPage}&limit=20`);
      
      if (response.episodes && response.episodes.length > 0) {
        setAllEpisodes(prev => [...prev, ...response.episodes]);
        setCurrentPage(nextPage);
        setHasMore(response.pagination.hasMore);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more episodes:', error);
    } finally {
      setIsLoadingMore(false);
      loadingRef.current = false;
    }
  };

  // 监听滚动到底部
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: '200px 0px',
  });

  // 当滚动到底部时加载更多
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore && !loadingRef.current) {
      loadMoreData();
    }
  }, [inView, hasMore, isLoadingMore]);

  // 格式化错误消息
  const errorMessage = initialError ? 
    (initialError instanceof Error ? initialError.message : "Failed to load episodes") : 
    null;

  const mobileMenuButton = (
    <MobileMenuButton ariaLabel={lang === 'chinese' ? '打开菜单' : 'Open menu'} />
  );

  if (isInitialLoading) {
    return (
      <div className="px-2">
        {mobileMenuButton}
        {/* Header */}
        <div className="mb-4 sm:mb-8 space-y-2">
          <Button variant="ghost" className="h-8 my-3 px-0" asChild>
            <Link href="/">
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('common.backToHome')}</span>
            </Link>
          </Button>

          <div className="space-y-2">
            <div className="h-8 bg-muted-foreground/20 rounded animate-pulse w-1/4"></div>
            <div className="h-5 bg-muted-foreground/20 rounded animate-pulse w-1/3"></div>
          </div>
        </div>

        {/* Episodes list skeleton */}
        <div className="space-y-2">
          <div className="flex justify-center">
            <SpinnerIcon size="lg" color="hsl(var(--muted-foreground))" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 18 }).map((_, index) => (
              <SkeletonItem key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="px-2">
        {mobileMenuButton}
        <div className="my-4">
          <Button variant="ghost" className="h-8 px-0" asChild>
            <Link href="/">
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('common.backToHome')}</span>
            </Link>
          </Button>
        </div>

        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{t('episodes.errorTitle')}</h1>
          <p className="text-muted-foreground mb-6">{errorMessage}</p>
          <Button onClick={() => window.location.reload()}>
            {t('episodes.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="px-2">
      {mobileMenuButton}
      {/* Header */}
      <div className="px-2 mb-4 sm:mb-8 space-y-2">
        <Button variant="ghost" className="h-8 my-3 px-0" asChild>
          <Link href="/">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('common.backToHome')}</span>
          </Link>
        </Button>

        <div className="space-y-2">
          <h1 className="text-3xl font-bold flex items-center">
            <ChartColumnIncreasing className="w-8 h-8 mr-3" strokeWidth={2.5} />
            {t('episodes.title')}
          </h1>
          <p className="text-muted-foreground">
            {lang === 'chinese' 
              ? `${allEpisodes.length} / ${totalEpisodes} 个节目已加载` 
              : `${allEpisodes.length} of ${totalEpisodes} episodes loaded`}
          </p>
        </div>
      </div>

      {/* Episodes list */}
      {allEpisodes.length > 0 ? (
        <div className="space-y-2">
          {/* Episodes grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {allEpisodes.map((episode, index) => (
              <div key={`${episode.shareId}-${index}`} className="w-full">
                <EpisodeItem episode={episode} isCompleted={isEpisodeCompleted(episode.shareId)} />
              </div>
            ))}
          </div>

          {/* Load more indicator */}
          {hasMore && (
                         <div ref={inViewRef} className="flex justify-center items-center py-8">
               {isLoadingMore ? (
                 <div className="flex items-center gap-2 text-muted-foreground">
                   <SpinnerIcon size="sm" color="currentColor" />
                   <span>{t('episodes.loadingMore')}</span>
                 </div>
               ) : (
              <Button 
                onClick={loadMoreData} 
                variant="outline" 
                disabled={isLoadingMore}
                className="min-w-[120px]"
              >
                  {t('episodes.loadMore')}
              </Button>
            )}
          </div>
          )}

          {/* No more episodes message */}
          {!hasMore && allEpisodes.length > 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {t('episodes.noMore')}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="flex justify-center mb-6">
            <SpinnerIcon size="lg" color="hsl(var(--muted-foreground))" />
          </div>
          <h2 className="text-xl font-semibold mb-4">{t('episodes.noneTitle')}</h2>
          <p className="text-muted-foreground">{t('episodes.noneDesc')}</p>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
